import React, { useEffect, useState } from 'react';
import { useAuth } from '../../components/AuthContext';
import { canAccessSpeakUpAdmin } from '../../lib/permissions';
import { supabase } from '../../lib/supabase';
import { BRANCHES, branchNameById, STATUS_LABEL, SENDER_LABEL } from './speakupConstants';
import SpeakUpFlyerCard from './SpeakUpFlyerCard';
import './speakup.css';

// Gate callout E1: LANES are the tabs, and concerns come first. The old tabs
// were New / In review / Resolved — a workflow state, which tells you nothing
// about what kind of thing you are about to open.
const LANE_TABS = [
  { value: 'concern', label: 'Concerns' },
  { value: 'idea', label: 'Ideas' },
  { value: 'resolved', label: 'Resolved' },
];

export default function SpeakUpAdminInbox() {
  const { profile, isAdmin } = useAuth();
  const allowed = canAccessSpeakUpAdmin({ isAdmin, profile });

  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [laneFilter, setLaneFilter] = useState('concern');
  const [selectedId, setSelectedId] = useState(null);

  // #479, OOB-6. The flyer controls STAY on this page and now ALSO appear on
  // the Speak Up oversight page, from one shared SpeakUpFlyerCard.
  //
  // The bug was never that they were here — it was that here was the ONLY
  // place. #472 narrowed this page to the assigned handler alone, correctly,
  // because it is where anonymous reports are read; the printing control
  // inherited a restriction written for something else, so the only person who
  // could produce a wall poster was the one person whose job is reading what
  // comes back. Colby found it by trying: "i cant scan a printed flyer. I'm
  // not the speak up admin."
  //
  // Removing them from here would have been the same bug pointed at somebody
  // else: the user menu hides the oversight entry from whoever holds this one,
  // so the handler would have lost the posters for her own programme.
  //
  // A control inherits the permission of the page it sits on. Putting a
  // harmless action inside a restricted surface silently restricts it — and
  // the answer is another door, not a wider room.

  async function loadList() {
    const { data, error: rpcError } = await supabase.rpc('admin_list_submissions');
    if (rpcError) { setError('load_error'); return; }
    setItems(data || []);
  }

  useEffect(() => { if (allowed) loadList(); }, [allowed]);

  if (!allowed) {
    return (
      <div className="speakup-admin-wrap">
        <div className="card"><div className="card-body">You don't have access to this section.</div></div>
      </div>
    );
  }

  if (error === 'load_error') {
    return (
      <div className="speakup-admin-wrap">
        <div className="card"><div className="card-body">Couldn't load the inbox. Try refreshing.</div></div>
      </div>
    );
  }

  if (selectedId) {
    return (
      <SpeakUpDetail
        id={selectedId}
        onBack={() => { setSelectedId(null); loadList(); }}
      />
    );
  }

  // Resolved is a tab of its own, so the two lane tabs show live work only.
  const all = items || [];
  const filtered = laneFilter === 'resolved'
    ? all.filter((i) => i.status === 'resolved')
    : all.filter((i) => i.lane === laneFilter && i.status !== 'resolved');

  const openConcerns = all.filter((i) => i.lane === 'concern' && i.status !== 'resolved').length;
  const openIdeas = all.filter((i) => i.lane === 'idea' && i.status !== 'resolved').length;
  const overdue = all.filter((i) => i.is_overdue).length;
  const laneCount = (v) => (v === 'resolved'
    ? all.filter((i) => i.status === 'resolved').length
    : all.filter((i) => i.lane === v && i.status !== 'resolved').length);

  return (
    <div className="speakup-admin-wrap">
      <div className="page-header">
        <div className="page-title">Speak Up</div>
        <div className="page-sub">
          {openConcerns} {openConcerns === 1 ? 'concern' : 'concerns'} open
          {overdue > 0 && <> · <strong className="speakup-overdue-text">{overdue} past the reply date</strong></>}
          {' '}· {openIdeas} {openIdeas === 1 ? 'idea' : 'ideas'}
        </div>
      </div>

      {/* #479. All four branches — the handler runs the programme company-wide. */}
      <SpeakUpFlyerCard />

      <div className="speakup-tabs">
        {LANE_TABS.map((t) => (
          <button
            key={t.value}
            className={laneFilter === t.value ? 'speakup-tab speakup-tab-active' : 'speakup-tab'}
            onClick={() => setLaneFilter(t.value)}
            type="button"
          >
            {t.label} · {laneCount(t.value)}
          </button>
        ))}
      </div>

      <div className="card">
        {items === null && <div className="card-body">Loading…</div>}
        {items && filtered.length === 0 && <div className="card-body text-muted">Nothing here.</div>}
        {filtered.map((row) => (
          <div key={row.id} className="speakup-row" onClick={() => setSelectedId(row.id)}>
            <div className="speakup-row-top">
              <span className="speakup-dot" />
              {/* Callout E2: age is a BADGE, not a date. "Reply overdue · 9
                  days" is the whole of D24 on the handler's side — a
                  timestamp does not accuse anyone of anything, and does not
                  tell you whether the queue is being worked. */}
              {row.is_overdue ? (
                <span className="speakup-age speakup-age-over">
                  Reply overdue · {row.awaiting_reply_days} days
                </span>
              ) : row.awaiting_reply_days !== null && row.awaiting_reply_days !== undefined ? (
                <span className="speakup-age">
                  Reply due in {Math.max(0, 7 - row.awaiting_reply_days)} days
                </span>
              ) : row.lane === 'concern' ? (
                <span className="speakup-age speakup-age-ok">Acknowledged</span>
              ) : null}
              <span className="speakup-chip speakup-chip-cat">{row.category}</span>
              <strong>{branchNameById(row.branch_id)}</strong>
              <span className="speakup-when">
                {new Date(row.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </span>
            </div>
            <div className="speakup-row-body">{row.body_snippet}</div>
            <div className="speakup-row-meta">
              {/* Callout E5: replaces "Auto-routed to hr", which named a role
                  that never existed in Stede and which nothing read. */}
              {row.has_thread ? 'Thread open' : 'One-time submission — no way to reply'}
              {row.visibility === 'published' ? ' · published' : ''}

              {/* Callout E3: the reporter's restriction is on the ROW, before
                  it is opened. It has to be visible at the moment of deciding
                  what to do, not discovered inside a share dialog after a
                  plan has already formed. */}
              {row.involves_management_chain && (
                <div className="speakup-row-flag">
                  Reporter asked not to share within {branchNameById(row.branch_id)}
                </div>
              )}

              {/* Callout E4: existing shares by name and date. Who else is
                  holding a piece of this is a fact about the case, not an
                  audit detail buried a click away. */}
              {row.shared_with && (
                <div className="speakup-row-shared">Shared with {row.shared_with}</div>
              )}

              {row.routed_to_alternate && (
                <div className="speakup-row-flag">
                  Reporter asked that the usual reader not see this
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SpeakUpDetail({ id, onBack }) {
  const [submission, setSubmission] = useState(null);
  const [messages, setMessages] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [error, setError] = useState('');

  async function loadDetail() {
    const [subRes, msgRes] = await Promise.all([
      supabase.rpc('admin_get_submission', { p_id: id }),
      supabase.rpc('admin_get_messages', { p_id: id }),
    ]);
    if (subRes.error || msgRes.error) { setError('load_error'); return; }
    setSubmission(Array.isArray(subRes.data) ? subRes.data[0] : subRes.data);
    setMessages(msgRes.data || []);
  }

  useEffect(() => { loadDetail(); }, [id]);

  async function handleReply(e) {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    const { error: rpcError } = await supabase.rpc('admin_reply', { p_id: id, p_body: reply.trim() });
    setSending(false);
    if (rpcError) { setError('reply_error'); return; }
    setReply('');
    loadDetail();
  }

  async function setStatus(newStatus) {
    await supabase.rpc('admin_set_status', { p_id: id, p_status: newStatus });
    loadDetail();
  }

  if (error === 'load_error') {
    return (
      <div className="speakup-admin-wrap">
        <div className="card"><div className="card-body">Couldn't load this submission.</div></div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="speakup-admin-wrap">
        <div className="card"><div className="card-body">Loading…</div></div>
      </div>
    );
  }

  return (
    <div className="speakup-admin-wrap">
      <button className="speakup-back" onClick={onBack} type="button">← Back to inbox</button>

      <div className="card" style={{ marginTop: 12 }}>
        <div className="card-header">
          <div>
            <strong>{branchNameById(submission.branch_id)}</strong>{' '}
            <span className="speakup-chip speakup-chip-cat">{submission.category}</span>{' '}
            <span className="speakup-chip speakup-chip-status">{STATUS_LABEL[submission.status]}</span>
          </div>
        </div>
        <div className="card-body">
          <p className="speakup-note" style={{ marginBottom: 12 }}>
            Anonymous thread · they check via their private link · you never see who they are
          </p>

          {/* #478, OOB-6. Labelled here as well as on the reporter's own
              screen, and for a reason of the handler's own: on an idea she can
              now be reading a conversation a branch manager is also answering,
              and "who said this" was not on the screen at all. Same labels the
              reporter sees, so the two views cannot describe the exchange
              differently. */}
          <div className="speakup-thread">
            {messages && messages.map((m, i) => {
              const mine = m.sender === 'submitter';
              const from = mine ? null : SENDER_LABEL[m.sender];
              return (
                <React.Fragment key={i}>
                  {from && <div className="speakup-msg-from">{from}</div>}
                  <div className={mine ? 'speakup-msg speakup-msg-them' : 'speakup-msg speakup-msg-company'}>
                    {m.body}
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {submission.has_thread ? (
            <>
              {error === 'reply_error' && <div className="speakup-error">Could not send that. Try again.</div>}
              <form onSubmit={handleReply} className="speakup-reply-row" style={{ marginTop: 12 }}>
                <input
                  className="speakup-input"
                  placeholder="Reply anonymously…"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                />
                <button className="speakup-btn-sm speakup-btn-dark" type="submit" disabled={sending}>
                  {sending ? '…' : 'Send'}
                </button>
              </form>
            </>
          ) : (
            <p className="speakup-note" style={{ marginTop: 12 }}>
              This submitter didn't opt into a follow-up thread — one-time submission.
            </p>
          )}

          <div className="speakup-action-row">
            {submission.status !== 'in_review' && (
              <button className="btn btn-secondary btn-sm" onClick={() => setStatus('in_review')} type="button">Set in review</button>
            )}
            {submission.status !== 'resolved' && (
              <button className="btn btn-secondary btn-sm" onClick={() => setStatus('resolved')} type="button">Mark resolved</button>
            )}
            {/* Sharing exists for CONCERNS only. A branch manager already reads
                their own branch's ideas as written (D4), so a share would be a
                second, weaker path to something they already have in full.
                admin_share_concern refuses an idea outright. */}
            {submission.lane === 'concern' && (
              <button className="btn btn-sm" onClick={() => setShowShare(true)} type="button">Share with someone…</button>
            )}
            <button className="btn btn-primary btn-sm" onClick={() => setShowPublish(true)} type="button">Publish resolution…</button>
          </div>
        </div>
      </div>

      {showShare && (
        <ShareModal
          submission={submission}
          onClose={() => setShowShare(false)}
          onShared={() => { setShowShare(false); loadDetail(); }}
        />
      )}

      {showPublish && (
        <PublishModal
          submission={submission}
          onClose={() => setShowPublish(false)}
          onPublished={() => { setShowPublish(false); loadDetail(); }}
        />
      )}
    </div>
  );
}

function PublishModal({ submission, onClose, onPublished }) {
  const [visibility, setVisibility] = useState(submission.visibility === 'published' ? 'published' : 'private');
  const [said, setSaid] = useState(submission.public_said || '');
  const [summary, setSummary] = useState(submission.public_summary || '');
  const [scope, setScope] = useState(submission.publish_scope ? 'choose' : 'this');
  const [chosenBranches, setChosenBranches] = useState(submission.publish_scope || [submission.branch_id]);
  const [markResolved, setMarkResolved] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pubError, setPubError] = useState('');

  function toggleBranch(bid) {
    setChosenBranches((prev) => (prev.includes(bid) ? prev.filter((x) => x !== bid) : [...prev, bid]));
  }

  async function handlePublish() {
    setSaving(true);
    if (visibility !== 'published') {
      await supabase.rpc('admin_set_status', { p_id: submission.id, p_status: 'resolved' });
      setSaving(false);
      onPublished();
      return;
    }
    let scopeArray = null;
    if (scope === 'this') scopeArray = [submission.branch_id];
    else if (scope === 'choose') scopeArray = chosenBranches;

    const { error: rpcError } = await supabase.rpc('admin_publish', {
      p_id: submission.id,
      p_summary: summary,
      p_scope: scopeArray,
      p_mark_resolved: markResolved,
      p_said: said.trim() || null,
    });
    setSaving(false);
    if (rpcError) {
      // The database refuses to publish a concern - gate decision D20, enforced
      // in admin_publish() rather than here, so no code path can forget it.
      setPubError(
        /cannot_publish_concern/.test(rpcError.message || '')
          ? 'Concerns cannot be published to the board. Only ideas can.'
          : 'Could not save that. Please try again.'
      );
      return;
    }
    onPublished();
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Publish resolution</div>
          <button className="close-btn" onClick={onClose} type="button">×</button>
        </div>
        <div className="modal-body">
          <label className="form-label">Original submission (never shown publicly)</label>
          <div className="speakup-quote" style={{ marginBottom: 14 }}>{submission.body}</div>

          <label className="form-label">Visibility</label>
          <div className="speakup-visibility-row">
            <button
              className={visibility === 'private' ? 'speakup-pick speakup-pick-on' : 'speakup-pick'}
              onClick={() => setVisibility('private')}
              type="button"
            >
              <div className="speakup-pick-t">Keep private</div>
              <div className="speakup-pick-d">Resolve without posting. Only the submitter sees it in their thread.</div>
            </button>
            <button
              className={visibility === 'published' ? 'speakup-pick speakup-pick-on' : 'speakup-pick'}
              onClick={() => setVisibility('published')}
              type="button"
            >
              <div className="speakup-pick-t">Publish summary</div>
              <div className="speakup-pick-d">Post a curated version to the public board.</div>
            </button>
          </div>

          {visibility === 'published' && (
            <>
              {/* Gate callout D1: the board card has two labelled halves.
                  One text box read as a noticeboard; split, it reads as
                  evidence that saying something changes something — which is
                  the only reason the board exists. Both halves are the
                  handler's words, never the reporter's. */}
              <label className="form-label" style={{ marginTop: 14 }}>You said — the point, in your words</label>
              <textarea
                className="form-textarea"
                value={said}
                onChange={(e) => setSaid(e.target.value)}
                placeholder="Summarise what was raised, without the original phrasing…"
              />
              <p className="form-hint" style={{ marginBottom: 14 }}>
                Rewriting protects anonymity — small-branch details in the original could identify the author.
                Leave this empty to post a single block instead of a split card.
              </p>

              <label className="form-label">We did — what changed</label>
              <textarea
                className="form-textarea"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="What was actually done about it…"
              />
              <p className="form-hint" style={{ marginBottom: 14 }}>
                This half is what makes the board worth reading. "We looked into it" is not a change.
              </p>

              <label className="form-label">Publish to</label>
              <div className="speakup-scope-row">
                <button className={scope === 'this' ? 'speakup-pill speakup-pill-on' : 'speakup-pill'} type="button" onClick={() => setScope('this')}>
                  {branchNameById(submission.branch_id)} only
                </button>
                <button className={scope === 'all' ? 'speakup-pill speakup-pill-on' : 'speakup-pill'} type="button" onClick={() => setScope('all')}>
                  All branches
                </button>
                <button className={scope === 'choose' ? 'speakup-pill speakup-pill-on' : 'speakup-pill'} type="button" onClick={() => setScope('choose')}>
                  Choose branches…
                </button>
              </div>
              {scope === 'choose' && (
                <div className="speakup-scope-row" style={{ marginTop: 8 }}>
                  {BRANCHES.map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      className={chosenBranches.includes(b.id) ? 'speakup-pill speakup-pill-on' : 'speakup-pill'}
                      onClick={() => toggleBranch(b.id)}
                    >
                      {b.name}
                    </button>
                  ))}
                </div>
              )}

              <label className="speakup-check" style={{ marginTop: 14 }}>
                <input type="checkbox" checked={markResolved} onChange={(e) => setMarkResolved(e.target.checked)} />
                <span>Mark as Resolved when published</span>
              </label>
            </>
          )}

          {pubError && <div className="speakup-error" style={{ marginTop: 14 }}>{pubError}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} type="button">Cancel</button>
          <button className="btn btn-primary" onClick={handlePublish} disabled={saving} type="button">
            {saving ? 'Publishing…' : visibility === 'published' ? `Publish to ${scope === 'all' ? 'all branches' : branchNameById(submission.branch_id)}` : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Gate stage F — sharing a concern, in two steps.
//
// F1: the summary box starts EMPTY, with the original quoted above it. Copied
// deliberately from the publish modal, which already solved this — prefilling
// the box with the raw text means somebody eventually shares without editing,
// and the whole protection evaporates in one click.
//
// F2: the exclusion explains itself WHERE IT BITES. Excluded managers are
// shown, greyed, with the reason on the row. A picker that silently omits
// people teaches the handler that the tool is broken.
//
// F3: the confirmation asks the one question software cannot answer. Angie is
// the only person who has read the report; the system knows the recipient's
// name and nothing about whether he is the subject. So it asks, by name, at
// the last possible moment.
//
// F4: the reason field says "recorded and permanent" on its face. Knowing the
// sentence is being kept changes the sentence.
// ---------------------------------------------------------------------------
function ShareModal({ submission, onClose, onShared }) {
  const [step, setStep] = useState(1);
  const [recipients, setRecipients] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [summary, setSummary] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    supabase.rpc('admin_list_share_recipients', { p_id: submission.id }).then(({ data, error: rpcError }) => {
      if (rpcError) { setError('Could not load the list of people.'); return; }
      setRecipients(data || []);
    });
  }, [submission.id]);

  const picked = (recipients || []).find((r) => r.user_id === chosen);
  const canContinue = chosen && summary.trim().length > 0 && reason.trim().length > 0;

  async function confirmShare() {
    setSaving(true);
    const { error: rpcError } = await supabase.rpc('admin_share_concern', {
      p_id: submission.id,
      p_recipient: chosen,
      p_summary: summary.trim(),
      p_reason: reason.trim(),
    });
    setSaving(false);
    if (rpcError) {
      const m = rpcError.message || '';
      setError(
        /recipient_excluded_by_reporter/.test(m)
          ? 'The reporter asked that this not be shared within their branch.'
          : /only_concerns_are_shared/.test(m)
            ? 'Only concerns can be shared. A branch manager already reads ideas in full.'
            : 'Could not share that. Please try again.'
      );
      setStep(1);
      return;
    }
    onShared();
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            {step === 1 ? 'Share a summary' : 'Confirm — this cannot be undone'}
          </div>
          <button className="close-btn" onClick={onClose} type="button">×</button>
        </div>

        {step === 1 && (
          <div className="modal-body">
            <div className="speakup-banner speakup-banner-strong">
              This is what they will see. They will not see the original.
            </div>

            <label className="form-label">The original — never shared</label>
            <div className="speakup-quote speakup-quote-locked">{submission.body}</div>

            <label className="form-label" style={{ marginTop: 14 }}>Your summary</label>
            <textarea
              className="form-textarea"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Describe what needs acting on, in your own words…"
            />
            <p className="form-hint" style={{ marginBottom: 14 }}>
              Rewriting protects the reporter — how somebody phrases a thing identifies them faster
              than the thing does.
            </p>

            <label className="form-label">Share with</label>
            {recipients === null && <div className="speakup-note">Loading…</div>}
            <div className="speakup-recipients">
              {(recipients || []).map((r) => (
                <button
                  key={r.user_id}
                  type="button"
                  disabled={!!r.excluded_reason}
                  className={
                    r.excluded_reason
                      ? 'speakup-recipient speakup-recipient-off'
                      : chosen === r.user_id
                        ? 'speakup-recipient speakup-recipient-on'
                        : 'speakup-recipient'
                  }
                  onClick={() => setChosen(r.user_id)}
                >
                  <div className="speakup-recipient-name">
                    {r.full_name}
                    {r.already_shared ? ' · already shared' : ''}
                  </div>
                  <div className="speakup-recipient-sub">
                    {r.role === 'executive'
                      ? 'Executive'
                      : `${branchNameById(r.branch_id)} branch manager`}
                    {r.excluded_reason ? ` — ${r.excluded_reason}` : ''}
                  </div>
                </button>
              ))}
            </div>

            <label className="form-label" style={{ marginTop: 14 }}>Why are you sharing this</label>
            <textarea
              className="form-textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Recorded and permanent…"
            />

            {error && <div className="speakup-error">{error}</div>}
          </div>
        )}

        {step === 2 && picked && (
          <div className="modal-body">
            <h3 className="speakup-h1" style={{ fontSize: 18 }}>
              Share a summary with {picked.full_name}?
            </h3>
            <p className="speakup-note" style={{ marginTop: 10 }}>
              {picked.full_name.split(' ')[0]} will see <strong>your summary only</strong>, the branch
              and the category. They will not see the original, will not be able to reply to the
              reporter, and will not be told who they are.
            </p>

            <div className="speakup-confirm-ask">
              <strong>Before you confirm:</strong> is this concern about {picked.full_name}, or about
              anyone who reports to them?
            </div>

            <label className="form-label" style={{ marginTop: 14 }}>They will see</label>
            <div className="speakup-quote">{summary.trim()}</div>

            {error && <div className="speakup-error">{error}</div>}
          </div>
        )}

        <div className="modal-footer">
          {step === 1 ? (
            <>
              <button className="btn btn-ghost" onClick={onClose} type="button">Cancel</button>
              <button
                className="btn btn-primary"
                type="button"
                disabled={!canContinue}
                onClick={() => { setError(''); setStep(2); }}
              >
                Continue
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-ghost" onClick={() => setStep(1)} type="button">Back</button>
              <button className="btn btn-primary" type="button" disabled={saving} onClick={confirmShare}>
                {saving ? 'Sharing…' : `Share with ${picked ? picked.full_name.split(' ')[0] : ''}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
